import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { MetricValue, Report } from '@hospasense/contracts';
import { fetchReport } from '../lib/queries-s5.js';
import {
  Badge,
  Card,
  CardBody,
  ErrorState,
  LoadingState,
  PageHeader,
} from '../components/ui/index.js';

const DRILL_THROUGH: Record<string, { to: string; label: string }> = {
  activation_rate: { to: '/onboarding', label: 'Onboarding pipeline' },
  time_to_value_days: { to: '/onboarding', label: 'Onboarding pipeline' },
  fleet_adoption_current_release: { to: '/releases', label: 'Releases & rollout' },
  retention_rate: { to: '/organisations', label: 'Organisation registry' },
  support_burden_per_org: { to: '/support', label: 'Support queue' },
  sla_attainment: { to: '/support', label: 'Support queue' },
  expansion_signals: { to: '/entitlements', label: 'Entitlement drift' },
  invoice_collection_rate: { to: '/billing', label: 'B2B invoicing' },
};

function formatValue(m: MetricValue): string {
  if (m.value === null) return '—';
  if (m.unit === 'percent') return `${m.value}%`;
  if (m.unit === 'days') return `${m.value} days`;
  if (m.unit === 'ratio') return `${m.value}×`;
  return String(m.value);
}

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchReport(id)
      .then(setReport)
      .catch((err: Error) => setError(err.message));
  }, [id]);

  if (error) return <ErrorState description={error} />;
  if (!report) return <LoadingState label="Loading report…" />;

  return (
    <div className="space-y-6" data-testid="report-detail-page">
      <PageHeader
        title={report.title}
        subtitle={`Immutable snapshot generated ${new Date(report.generatedAt).toLocaleString()} (${report.generatedBy}) for ${new Date(report.periodStart).toLocaleDateString()} – ${new Date(report.periodEnd).toLocaleDateString()}. Stored figures are exactly what readers saw; nothing recomputes on read.`}
        actions={
          report.hasIncompleteSources ? (
            <Badge tone="warning">incomplete sources flagged inside</Badge>
          ) : (
            <Badge tone="success">all sources complete</Badge>
          )
        }
      />
      <p className="text-xs text-text-muted">
        <Link to="/reports" className="text-link hover:underline">
          ← All reports
        </Link>
      </p>
      {report.sections.map((section) => (
        <section key={section.title} className="space-y-3">
          <h2 className="text-lg font-bold text-text-primary">{section.title}</h2>
          {section.narrative && <p className="text-sm text-text-secondary">{section.narrative}</p>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {section.metrics.map((m) => (
              <Card key={m.key}>
                <CardBody>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary">{m.title}</h3>
                      <p
                        className={`mt-1 text-2xl font-bold tabular-nums ${m.incomplete ? 'text-text-muted' : 'text-text-primary'}`}
                      >
                        {formatValue(m)}
                      </p>
                    </div>
                    {m.incomplete && <Badge tone="warning">incomplete</Badge>}
                  </div>
                  {m.incomplete && m.incompleteReason && (
                    <p className="mt-1 text-xs text-warning">{m.incompleteReason}</p>
                  )}
                  <dl className="mt-2 space-y-0.5 border-t border-border pt-2 text-xs text-text-secondary">
                    <div>
                      <span className="font-semibold text-text-muted">Numerator:</span>{' '}
                      {m.numerator}
                      {m.numeratorValue !== null && (
                        <span className="font-mono text-text-muted"> (= {m.numeratorValue})</span>
                      )}
                    </div>
                    <div>
                      <span className="font-semibold text-text-muted">Denominator:</span>{' '}
                      {m.denominator}
                      {m.denominatorValue !== null && (
                        <span className="font-mono text-text-muted"> (= {m.denominatorValue})</span>
                      )}
                    </div>
                    <div>
                      <span className="font-semibold text-text-muted">Timeframe:</span>{' '}
                      {m.timeframe}
                    </div>
                    <div>
                      <span className="font-semibold text-text-muted">Exclusions:</span>{' '}
                      {m.exclusions}
                    </div>
                    <div className="font-mono">
                      <span className="font-sans font-semibold text-text-muted">Source:</span>{' '}
                      {m.sourceOfTruth}
                    </div>
                  </dl>
                  <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
                    <span className="text-text-muted">
                      Freshness: computed {new Date(m.computedAt).toLocaleString()}
                    </span>
                    {DRILL_THROUGH[m.key] && (
                      <Link
                        to={DRILL_THROUGH[m.key]!.to}
                        className="font-medium text-link hover:underline"
                      >
                        {DRILL_THROUGH[m.key]!.label} →
                      </Link>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
